import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Grid2 as Grid, Chip,
  Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, AlertTitle, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, Collapse, IconButton, Tooltip, Stack,
} from '@mui/material';
import {
  AccountBalance as LedgerIcon, Refresh as RefreshIcon, Add as AddIcon,
  KeyboardArrowDown, KeyboardArrowUp, HealthAndSafety as CheckIcon,
} from '@mui/icons-material';
import {
  ledgerAdminService, ContaLedger, Lancamento, Verificacao,
  TipoConta, ClasseFundos, DonoConta,
} from '../../services/ledger.service';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;

const TIPO_LABEL: Record<string, string> = {
  ASSET: 'Activo', LIABILITY: 'Passivo', REVENUE: 'Receita', EXPENSE: 'Despesa',
};
const TIPO_COR: Record<string, 'primary' | 'warning' | 'success' | 'error'> = {
  ASSET: 'primary', LIABILITY: 'warning', REVENUE: 'success', EXPENSE: 'error',
};

function LinhaLancamento({ l }: { l: Lancamento }) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setAberto(!aberto)}>
            {aberto ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {new Date(l.criadoEm).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
        </TableCell>
        <TableCell><Chip size="small" label={l.evento} variant="outlined" sx={{ fontSize: 10 }} /></TableCell>
        <TableCell sx={{ fontSize: 13 }}>{l.descricao ?? '—'}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {cve(l.debito)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
          <Collapse in={aberto} unmountOnExit>
            <Box sx={{ py: 2, px: 4 }}>
              <Table size="small">
                <TableBody>
                  {l.linhas.map((x, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ width: 90, fontSize: 11, fontWeight: 600,
                        color: x.direccao === 'DEBIT' ? 'primary.main' : 'text.secondary' }}>
                        {x.direccao === 'DEBIT' ? 'DÉBITO' : 'CRÉDITO'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{x.conta}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {cve(x.montante)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {x.refTipo ? `${x.refTipo} · ${x.refId}` : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Ledger de dupla entrada.
 *
 * O plano de contas só se tocava por migração e os lançamentos só se viam por
 * SQL — o que tornava invisível a contabilidade de todo o dinheiro que passa
 * pela plataforma.
 */
export default function LedgerPage() {
  const [aba, setAba] = useState(0);
  const [contas, setContas] = useState<ContaLedger[]>([]);
  const [totais, setTotais] = useState<any>(null);
  const [opcoes, setOpcoes] = useState<any>(null);
  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [eventos, setEventos] = useState<string[]>([]);
  const [integridade, setIntegridade] = useState<{ ok: boolean; verificacoes: Verificacao[]; coberturaSegregadaCVE: number; resumo: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filtroEvento, setFiltroEvento] = useState('');
  const [filtroConta, setFiltroConta] = useState('');

  const [novaAberta, setNovaAberta] = useState(false);
  const [nova, setNova] = useState({ id: '', accountType: 'ASSET', fundClass: 'OWN', ownerType: 'PLATFORM' });
  const [aCriar, setACriar] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await ledgerAdminService.contas();
      setContas(c.contas);
      setTotais(c.totais);
      setOpcoes(c.opcoes);

      const l = await ledgerAdminService.lancamentos({
        evento: filtroEvento || undefined,
        conta: filtroConta || undefined,
        limit: 25,
      });
      setLancamentos(l.linhas);
      setEventos(l.eventos);

      setIntegridade(await ledgerAdminService.integridade());
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível carregar o ledger.');
    } finally {
      setLoading(false);
    }
  }, [filtroEvento, filtroConta]);

  useEffect(() => { load(); }, [load]);

  const criar = async () => {
    setACriar(true);
    setError(null);
    try {
      const r = await ledgerAdminService.criarConta(nova as any);
      setSuccess([r.message, r.aviso].filter(Boolean).join(' '));
      setNovaAberta(false);
      setNova({ id: '', accountType: 'ASSET', fundClass: 'OWN', ownerType: 'PLATFORM' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível criar a conta.');
    } finally {
      setACriar(false);
    }
  };

  if (loading && !contas.length) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <LedgerIcon color="primary" /> Ledger
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plano de contas e lançamentos de dupla entrada de tudo o que move dinheiro.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setNovaAberta(true)}>Nova conta</Button>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* O equilíbrio é a propriedade que não pode falhar: se débitos ≠ créditos,
          há dinheiro por explicar e nada mais na página interessa. */}
      {totais && (
        <Alert severity={totais.equilibrado ? 'success' : 'error'}>
          <AlertTitle>
            {totais.equilibrado ? 'Ledger equilibrado' : 'LEDGER DESEQUILIBRADO — incidente grave'}
          </AlertTitle>
          Débitos {cve(totais.debito)} · Créditos {cve(totais.credito)}
          {!totais.equilibrado && ` · diferença de ${cve(Math.abs(totais.debito - totais.credito))}`}
        </Alert>
      )}

      <Tabs value={aba} onChange={(_, v) => setAba(v)}>
        <Tab label={`Plano de contas (${contas.length})`} />
        <Tab label="Lançamentos" />
        <Tab label="Integridade" icon={integridade && !integridade.ok ? <CheckIcon color="error" /> : undefined} iconPosition="end" />
      </Tabs>

      {aba === 0 && (
        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Conta</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Fundos</TableCell>
                    <TableCell>Dono</TableCell>
                    <TableCell align="right">Débito</TableCell>
                    <TableCell align="right">Crédito</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contas.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                        {c.id}
                        {!c.emUso && (
                          <Tooltip title="Sem lançamentos ainda">
                            <Chip size="small" label="por usar" variant="outlined" sx={{ ml: 1, fontSize: 9 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={TIPO_LABEL[c.tipo] ?? c.tipo}
                          color={TIPO_COR[c.tipo] ?? 'default'} variant="outlined" sx={{ fontSize: 10 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {c.fundClass === 'SEGREGATED' ? 'Segregados' : 'Próprios'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.donoTipo}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                        {cve(c.debito)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                        {cve(c.credito)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                        {cve(c.saldo)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              O saldo é apresentado no sentido natural de cada conta — activos e despesas a débito,
              passivos e receitas a crédito. Vem sempre dos lançamentos, nunca de um campo guardado.
            </Typography>
          </CardContent>
        </Card>
      )}

      {aba === 1 && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" select label="Evento"
                  value={filtroEvento} onChange={(e) => setFiltroEvento(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {eventos.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" select label="Conta"
                  value={filtroConta} onChange={(e) => setFiltroConta(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {contas.map((c) => <MenuItem key={c.id} value={c.id}>{c.id}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>

            {lancamentos.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Sem lançamentos no período.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Data</TableCell>
                      <TableCell>Evento</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Montante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lancamentos.map((l) => <LinhaLancamento key={l.id} l={l} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {aba === 2 && integridade && (
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity={integridade.ok ? 'success' : 'error'}>{integridade.resumo}</Alert>

            <Box>
              <Typography variant="caption" color="text.secondary">Cobertura dos fundos segregados</Typography>
              <Typography variant="h5" fontWeight={700}
                color={integridade.coberturaSegregadaCVE >= 0 ? 'success.main' : 'error.main'}
                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {cve(integridade.coberturaSegregadaCVE)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Activos segregados menos passivos segregados. Negativo significa fundos de clientes a descoberto.
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  {integridade.verificacoes.map((v) => (
                    <TableRow key={v.check}>
                      <TableCell sx={{ width: 60 }}>
                        <Chip size="small" label={v.ok ? 'OK' : 'FALHA'}
                          color={v.ok ? 'success' : 'error'} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{v.check}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{v.detail ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary">
              As mesmas verificações correm sozinhas todos os dias às 04:00. Uma falha levanta alerta
              de compliance em Gestão → Alertas de Fraude.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Dialog open={novaAberta} onClose={() => setNovaAberta(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova conta do ledger</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Criar a conta torna-a utilizável em lançamentos, mas <strong>não a liga a nenhum fluxo</strong>.
              Para ser usada, o código que gera esse lançamento tem de a referenciar.
            </Alert>
            <TextField fullWidth size="small" label="Código"
              placeholder="Ex.: LOAN_RECEIVABLE"
              value={nova.id}
              onChange={(e) => setNova({ ...nova, id: e.target.value.toUpperCase() })}
              helperText="Maiúsculas, números e underscore. Nunca muda depois de criada — é a chave dos lançamentos." />
            <TextField fullWidth size="small" select label="Tipo"
              value={nova.accountType} onChange={(e) => setNova({ ...nova, accountType: e.target.value })}
              helperText="Activo e despesa vivem a débito; passivo e receita a crédito.">
              {(opcoes?.tipos ?? []).map((t: TipoConta) => (
                <MenuItem key={t} value={t}>{TIPO_LABEL[t]} ({t})</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" select label="Classe de fundos"
              value={nova.fundClass} onChange={(e) => setNova({ ...nova, fundClass: e.target.value })}
              helperText="Segregados entram na verificação de cobertura. Dinheiro emprestado pela Pagali é próprio.">
              {(opcoes?.classes ?? []).map((c: ClasseFundos) => (
                <MenuItem key={c} value={c}>{c === 'SEGREGATED' ? 'Segregados' : 'Próprios'} ({c})</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" select label="Dono"
              value={nova.ownerType} onChange={(e) => setNova({ ...nova, ownerType: e.target.value })}>
              {(opcoes?.donos ?? []).map((d: DonoConta) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovaAberta(false)} disabled={aCriar}>Cancelar</Button>
          <Button variant="contained" onClick={criar} disabled={aCriar || nova.id.length < 3}>
            {aCriar ? 'A criar…' : 'Criar conta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
